/**
 * Centralized route path constants and post-login routing helper.
 *
 * Implements US-003 T2: eliminates hardcoded navigate() path literals scattered
 * across App.tsx and LoginPage.tsx (enforces TO-LL-013 / TO-LL-014 — no new
 * hardcoded navigate paths; all path strings reference ROUTES).
 *
 * getPostLoginRoute is a pure, dependency-free function so it is trivially
 * unit-testable without a router context.
 *
 * Parent precedence rule: if groups includes both 'parent' and 'student' (multi-
 * group edge case), parent wins and PARENT_DASHBOARD is returned. This is documented
 * here as the authoritative precedence decision for US-003.
 *
 * Future group values (e.g. 'admin') not handled here → fall through to STUDENT_HOME.
 * That is the safe default for MVP; extend this function when new roles are added.
 */

// US-003 T2: ROUTES constant — single source of truth for all SPA path literals.
// Every navigate() call in the app must reference an entry here, never a string literal.
// Enforces TO-LL-013 (no hardcoded navigate paths).
export const ROUTES = {
  LOGIN: '/login',
  REGISTER: '/register',
  VERIFY_EMAIL: '/verify-email',
  FORGOT_PASSWORD: '/forgot-password',
  STUDENT_HOME: '/dashboard',
  SESSIONS: '/sessions',
  // US-003 context note: /parent is the deployed parent landing route (US-006).
  // PARENT_DASHBOARD points to /parent; US-008 will extend this with /parent/dashboard
  // sub-routes when the full ParentDashboardPage is delivered.
  PARENT_DASHBOARD: '/parent',
  // TS-017: parent-only top-up coin detail screen, opened from the dashboard coins card.
  PARENT_TOPUP_COINS: '/parent/topup-coins',
  // TS-007 (SR6): parent-only live-sessions listing screen, opened from the Overview
  // tab's "View all live sessions" link (shown only when liveSessions.length > 1).
  PARENT_LIVE_SESSIONS: '/parent/live-sessions',
  // Parent-only read-only transcript of a child's session, opened from the Reports
  // tab's "View Session" button. Parameterised — navigate via parentSessionPath().
  PARENT_SESSION: '/parent/session/:sessionId',
  // google-sign-in/US-001 (T1/T6, AC-001.2/AC-001.3): the Cognito Hosted-UI Google
  // OAuth redirect target. Public route (outside AuthGuard) — the user is
  // mid-authentication when this loads. Also used to build the Amplify
  // redirectSignIn URL (amplifyConfig.ts buildRedirectUrls) — kept as a single
  // source of truth so the registered route and the OAuth redirect URL can never
  // drift apart (TO-LL-013: no hardcoded navigate/path literals).
  OAUTH_CALLBACK: '/auth/callback',
  // show-not-tell/US-003 (T4, AC-003.2): the writing-coach doorway destination.
  // Registered here as the single source of truth (TO-LL-013) so the hero
  // card's "Start writing practice" button never hardcodes a path literal.
  // The actual page + FeatureGuard re-entitlement check are US-004/TS-004's
  // real deliverable; this constant only reserves the path.
  WRITING_PRACTICE: '/writing-practice',
  // show-not-tell/TS-014 (T6, AC-TS014.4): the dedicated full writing-history
  // page destination, reached from the WritingPracticeTile's "See all my
  // writing sessions" link (TO-LL-013 — single source of truth, never a
  // hardcoded path literal).
  // BUG-20260718-154634 (C-1/C-4): also the show-not-tell dashboard hero
  // card's (WritingPracticeHeroCard) primary CTA target — the page at this
  // route was upgraded (C-2) into the writing-sessions LANDING page
  // (start-new + resume-active + history), mirroring ROUTES.SESSIONS'
  // (SessionsListPage) role for comprehension. Reused here, not
  // re-declared (TO-LL-013).
  WRITING_PRACTICE_HISTORY: '/writing-practice/history',
  // BUG-20260722-000251 (T-12, ISS-001): these registered routes were previously
  // inline App.tsx path literals absent from ROUTES, so the new client-error
  // reporter's routePatternFromPath() (frontend/src/lib/errorReporter.ts) could
  // only derive 'unknown' for them — degrading the single most important
  // tutoring route to an unhelpful, privacy-safe-but-useless pattern. Centralised
  // here (single source of truth, TO-LL-013) so App.tsx (T-3) and the reporter
  // (T-1) share ONE registered route-pattern set. No path VALUE changes.
  SESSION_CHAT: '/session/:sessionId',
  MATH_COACH: '/math-coach',
  MATH_COACH_GUIDANCE: '/math-coach/guidance',
  VERIFY_ALIAS: '/verify',
  RESET_PASSWORD: '/reset-password',
  FORGOT_PASSWORD_VERIFY: '/forgot-password/verify',
  ROOT: '/',
} as const;

/**
 * Build the concrete path for a parent's read-only view of a child's session.
 *
 * ROUTES.PARENT_SESSION carries the ':sessionId' placeholder for route matching;
 * callers must navigate with this helper rather than interpolating a path literal
 * (TO-LL-013 — no hardcoded navigate paths).
 *
 * @param sessionId - the session to display
 * @returns absolute SPA path, e.g. '/parent/session/sess_abc123'
 */
export function parentSessionPath(sessionId: string): string {
  return ROUTES.PARENT_SESSION.replace(':sessionId', encodeURIComponent(sessionId));
}

/**
 * Build the concrete path for a student's tutoring-chat session.
 *
 * BUG-20260722-000251 (T-12): mirrors parentSessionPath() above for the student
 * SESSION_CHAT route. Provided for future call sites (TO-LL-013) — existing
 * '/session/${id}' navigation call sites (AppLogo.tsx, SessionChatPage) are
 * intentionally NOT refactored to use this helper as part of this bug fix
 * (out of scope; unrelated to the error reporter).
 *
 * @param sessionId - the session to open
 * @returns absolute SPA path, e.g. '/session/sess_abc123'
 */
export function sessionChatPath(sessionId: string): string {
  return ROUTES.SESSION_CHAT.replace(':sessionId', encodeURIComponent(sessionId));
}

/**
 * Compute the post-login destination from the user's Cognito groups.
 *
 * US-003 T2: implements the parent/student routing fork (AC-1, AC-2, AC-4).
 * Groups are derived from the /auth/login response (TS-001 injects cognito:groups).
 *
 * @param groups - optional string[] of Cognito group names for the authenticated user
 * @returns the absolute SPA path to navigate to immediately after a successful login
 */
export function getPostLoginRoute(groups?: string[]): string {
  // Coalesce undefined/null to [] so the check never throws (handles legacy persisted users).
  const safeGroups = groups ?? [];
  return safeGroups.includes('parent') ? ROUTES.PARENT_DASHBOARD : ROUTES.STUDENT_HOME;
}
